import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { currentPersonSelector, setCurrentPerson } from "@/pages/ContactsPage/slice";
import {
  useGetAllPersonsQuery,
  useLazyGetPersonByIdQuery,
  useUpdatePersonMutation,
  type Person,
} from "@/services/api";
import { useRuntimeImage } from "@/entities/runtime-image";
import { getErrorMessage } from "@/services/errors";
import { useAlert } from "@/shared/hooks";
import type { Key } from "react";
import type { ConnectionsColumnsState, ConnectionsTableData } from "../model/types";
import type { TableRowSelection } from "antd/es/table/interface";
import type { UpdatePersonRequest } from "@/services/api/entities/person/types";
import { connectionsTableSliceActions, connectionsTableSliceSelectors } from "../model";
import { getConnectionsTableColumns } from "../model/connections-table-columns";
import { buildConnectionsColumns } from "./build-connections-columns";
import { transformConnectionsColumnsStateToArray } from "./transform-connections-columns-state-to-array";

const buildTableData = ({
  allPersons,
  currentPerson,
}: {
  allPersons: Person[];
  currentPerson: Person;
}): ConnectionsTableData[] => {
  return currentPerson.personConnections.flatMap((item) => {
    const person = allPersons.find((personItem) => item.personId === personItem.id);
    const connectionPerson = allPersons.find(
      (connectionPersonItem) => item.connectionPersonId === connectionPersonItem.id,
    );
    if (!person || !connectionPerson) return [];
    return { ...item, person: person, connectionPerson: connectionPerson };
  });
};

export const useConnectionsTable = () => {
  const dispatch = useAppDispatch();
  const { getRuntimeImageURL } = useRuntimeImage();
  const { data: personsResponse, isLoading: personsResponseIsLoading } = useGetAllPersonsQuery();
  const [updatePerson] = useUpdatePersonMutation();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const currentPerson = useAppSelector(currentPersonSelector);
  const { callError } = useAlert();

  const tableColumnsState = useAppSelector(connectionsTableSliceSelectors.getColumnsState);
  const deleteModalIsOpen = useAppSelector(connectionsTableSliceSelectors.getDeleteModalIsOpen);
  const editModalIsOpen = useAppSelector(connectionsTableSliceSelectors.getEditModalIsOpen);
  const createModalIsOpen = useAppSelector(connectionsTableSliceSelectors.getCreateModalIsOpen);
  const settingsModalIsOpen = useAppSelector(connectionsTableSliceSelectors.getSettingsModalIsOpen);
  const selectedIds = useAppSelector(connectionsTableSliceSelectors.getSelectedConnecionsIds);

  const closeSettingsModal = () => {
    dispatch(connectionsTableSliceActions.closeSettingsModal());
  };

  const openSettingsModal = () => {
    dispatch(connectionsTableSliceActions.openSettingsModal());
  };

  const onSelectChange = (selectedKeys: Key[]) =>
    dispatch(connectionsTableSliceActions.setSelectedConnectionsIds(selectedKeys));

  const tableData = currentPerson
    ? buildTableData({
        allPersons: personsResponse?.persons ?? [],
        currentPerson: currentPerson,
      })
    : [];

  const tableColumns = buildConnectionsColumns({
    columns: getConnectionsTableColumns({
      getAvatar: (person) => getRuntimeImageURL({ fileId: person.avatarFile?.fileId ?? "" }),
    }),
    columnsKeys: transformConnectionsColumnsStateToArray({ state: tableColumnsState }),
    settingsButtonOnClick: openSettingsModal,
  });

  const rowSelection: TableRowSelection<ConnectionsTableData> = useMemo(
    () => ({
      selectedIds,
      onChange: onSelectChange,
      columnWidth: 35,
      defaultSelectedRowKeys: selectedIds,
    }),
    [selectedIds],
  );

  const closeDeleteModal = () => {
    dispatch(connectionsTableSliceActions.closeDeleteModal());
  };

  const openDeleteModal = () => {
    dispatch(connectionsTableSliceActions.openDeleteModal());
  };

  const openCreateModal = () => {
    dispatch(connectionsTableSliceActions.openCreateModal());
  };

  const closeCreateModal = () => {
    dispatch(connectionsTableSliceActions.closeCreateModal());
  };

  const openEditModal = () => {
    dispatch(connectionsTableSliceActions.openEditModal());
  };

  const closeEditModal = () => {
    dispatch(connectionsTableSliceActions.closeEditModal());
  };

  const setSelectedConnectionsIds = (connectionsIds: Key[]) => {
    dispatch(connectionsTableSliceActions.setSelectedConnectionsIds(connectionsIds));
  };

  const setColumnsState = (columnsKeys: ConnectionsColumnsState) => {
    dispatch(connectionsTableSliceActions.setColumnsState(columnsKeys));
  };

  const setNewCurrentPerson = (person: Person) => {
    dispatch(setCurrentPerson(person));
  };

  const updatePersonHandler = (updateData: UpdatePersonRequest) => {
    if (!currentPerson) return;
    getPersonById({ id: currentPerson.id })
      .unwrap()
      .then(({ lastModifiedDate }) => {
        updatePerson({ ...updateData, lastModifiedDate: lastModifiedDate })
          .unwrap()
          .then((person) => {
            setNewCurrentPerson(person);
          })
          .catch((e) => {
            callError({ text: getErrorMessage(e.code) });
          });
      })
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      });
  };

  const deleteSelectedConnections = (deletedIds: string[]) => {
    if (!currentPerson) return;
    const personConnections = [...currentPerson.personConnections].filter((personConnection) => {
      if (deletedIds.find((id) => id === personConnection.id)) return false;
      return true;
    });
    setSelectedConnectionsIds(
      selectedIds.filter((item) => {
        !!deletedIds.find((deletedId) => deletedId === item);
      }),
    );
    updatePersonHandler({ ...currentPerson, personConnections });
  };

  const createNewPersonConnection = (
    personConnection: Omit<Person["personConnections"][number], "id">,
  ) => {
    if (!currentPerson) return;
    const personConnections = [...currentPerson.personConnections, personConnection];
    updatePersonHandler({ ...currentPerson, personConnections });
  };

  const handleClickRow = useCallback(
    (row: ConnectionsTableData) => {
      dispatch(connectionsTableSliceActions.setSelectedConnectionsIds([...selectedIds, row.id]));
    },
    [dispatch],
  );

  const editPersonConnection = (personConnection: Person["personConnections"][number]) => {
    if (!currentPerson) return;
    const editablePersonConnectionIndex = [...currentPerson.personConnections].findIndex(
      (item) => item.id === personConnection.id,
    );
    if (!Number.isInteger(editablePersonConnectionIndex)) return;
    const personConnections = [...currentPerson.personConnections];
    personConnections[editablePersonConnectionIndex] = personConnection;
    updatePersonHandler({ ...currentPerson, personConnections });
  };

  return {
    state: {
      tableData,
      tableColumns,
      isLoading: personsResponseIsLoading,
      tableColumnsState,
      deleteModalIsOpen,
      settingsModalIsOpen,
      editModalIsOpen,
      createModalIsOpen,
      selectedIds,
      rowSelection,
    },
    methods: {
      closeSettingsModal,
      openSettingsModal,
      onSelectChange,
      setColumnsState,
      handleClickRow,
      setSelectedConnectionsIds,
      createNewPersonConnection,
      editPersonConnection,
      deleteSelectedConnections,
      openDeleteModal,
      closeDeleteModal,
      openCreateModal,
      closeCreateModal,
      openEditModal,
      closeEditModal,
    },
  };
};
